/**
 * Banker Reconciliation PDF Generator
 *
 * Generates a professional PDF summary of the banker reconciliation:
 * - Loan information and key dates
 * - Pricing summary with variance analysis
 * - Revenue and cost breakdowns
 * - LO compensation details
 * - Wire reconciliation status
 */

import jsPDF from 'jspdf';
import type { EncompassLoan } from '@/components/broker-recon/LoanAutocomplete';
import type {
  KeyDates,
  PricingState,
  CompensationState,
  CostsState,
  RevenueState,
  UploadedDocument
} from '@/components/banker-recon/types';
import type { PurchaseAdjustment } from '@/components/banker-recon/InvestorAdviceTab';
import type { BankedReconLineItem } from './bankedNetProfit';
import type { FhaUfmipBridge } from './fhaUfmip';
import { isBuydownPassThrough } from './buydownPassThrough';
import { MI_PREMIUM_REMITTANCE_LINE_TYPE } from './leFeeCanonical';
import {
  MANUAL_ADJUSTMENT_KIND_LABELS,
  manualAdjustmentFromLineItem,
  type ManualAdjustmentKind,
  type ManualReconAdjustment
} from './manualAdjustments';
import {
  calculateCdCreditNettedToWarehouseAdvance,
  calculateExpectedBalance,
  calculateInvestorWireAudit,
  isGovernmentFundingPassThrough
} from './wireReconciliation';

// Colors
const COLORS = {
  brandGreen: [22, 163, 74] as [number, number, number],
  black: [0, 0, 0] as [number, number, number],
  white: [255, 255, 255] as [number, number, number],
  grayHeader: [220, 220, 220] as [number, number, number],
  grayText: [100, 100, 100] as [number, number, number],
  lightGray: [245, 245, 245] as [number, number, number],
  border: [180, 180, 180] as [number, number, number],
  green: [22, 163, 74] as [number, number, number],
  red: [220, 38, 38] as [number, number, number],
  cyan: [6, 182, 212] as [number, number, number],
  violet: [139, 92, 246] as [number, number, number]
};

// Page dimensions (Letter size in points)
const PAGE = {
  width: 612,
  height: 792,
  marginLeft: 36,
  marginRight: 36,
  marginTop: 36,
  marginBottom: 50,
  get contentWidth() {
    return this.width - this.marginLeft - this.marginRight;
  }
};

// Data interface for PDF generation
export interface BankerReconPdfData {
  selectedLoan: EncompassLoan;
  status?: 'draft' | 'finalized' | 'pushed';
  keyDates: KeyDates;
  pricing: PricingState;
  compensation: CompensationState;
  costs: CostsState;
  revenue: RevenueState;
  lineItems: BankedReconLineItem[];
  fhaUfmipBridge: FhaUfmipBridge;
  totalWarehouseCosts: number;
  totalInvestorCosts: number;
  totalLoanRevenue: number;
  totalInvestorRevenue: number;
  totalRevenueForDisplay: number;
  totalCostsForDisplay: number;
  loPayout: number;
  volumeBonusAmount?: number;
  volumeBonusInstallment?: { number: number; total: number; allocated: boolean };
  companyRevenue: number;
  gosAmount: number;
  actualPurchaseDollar: number;
  netProfit: number;
  totalReimbursable: number;
  wireFromInvestor: number;
  wireFromWarehouse: number;
  servicingPrincipalCollected: number;
  lenderCreditTimingBasis?: 'field4794' | 'total';
  retainedClosingFeeBasis?: 'legacy' | 'expandedLeFees';
  uploadedDocuments: UploadedDocument[];
  purchaseAdjustments: PurchaseAdjustment[];
  overrides: Array<{ recipientName?: string | null; bps: number }>;
  totalOverrideAmount: number;
}

// Helper functions
function formatCurrency(amount: number): string {
  const absAmount = Math.abs(amount);
  const formatted = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(absAmount);
  return amount < 0 ? `(${formatted})` : formatted;
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) {
return '—';
}
  const [year, month, day] = dateStr.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  if (isNaN(date.getTime())) {
return '—';
}
  return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
}

function calculateDaysBetween(start: string, end: string): number {
  if (!start || !end) {
return 0;
}
  const [sy, sm, sd] = start.split('-').map(Number);
  const [ey, em, ed] = end.split('-').map(Number);
  const startDate = new Date(sy, sm - 1, sd);
  const endDate = new Date(ey, em - 1, ed);
  if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
return 0;
}
  const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
}

/**
 * Generate the Banker Reconciliation PDF
 */
export function generateBankerReconPDF(data: BankerReconPdfData): jsPDF {
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'pt',
    format: 'letter'
  });

  let y = PAGE.marginTop;

  // Header
  y = drawHeader(doc, data, y);

  // Loan Information
  y = drawLoanInfoSection(doc, data, y);

  // Key Dates
  y = drawKeyDatesSection(doc, data, y);

  // Pricing Summary
  y = drawPricingSummary(doc, data, y);

  // Check for page break
  if (y > PAGE.height - 250) {
    doc.addPage();
    y = PAGE.marginTop;
  }

  // Summary Totals (Revenue, Costs, Net Profit)
  y = drawSummaryTotals(doc, data, y);

  // LO Compensation
  y = drawCompensationSection(doc, data, y);

  // Check for page break
  if (y > PAGE.height - 200) {
    doc.addPage();
    y = PAGE.marginTop;
  }

  // Costs Breakdown
  y = drawCostsBreakdown(doc, data, y);

  // Check for page break
  if (y > PAGE.height - 200) {
    doc.addPage();
    y = PAGE.marginTop;
  }

  // Revenue Breakdown
  y = drawRevenueBreakdown(doc, data, y);

  // Check for page break
  if (y > PAGE.height - 150) {
    doc.addPage();
    y = PAGE.marginTop;
  }

  // Manual Adjustments (only when the recon has entries)
  const manualAdjustmentsEndY = drawManualAdjustments(doc, data, y);
  if (manualAdjustmentsEndY !== y) {
    y = manualAdjustmentsEndY;
    if (y > PAGE.height - 150) {
      doc.addPage();
      y = PAGE.marginTop;
    }
  }

  // Wire Reconciliation
  if (data.selectedLoan.trans_details_loan_type?.toUpperCase() === 'FHA') {
    y = drawFhaUfmipSection(doc, data, y);
    if (y > PAGE.height - 150) {
      doc.addPage();
      y = PAGE.marginTop;
    }
  }

  y = drawWireReconciliation(doc, data, y);

  // Document Status
  drawDocumentStatus(doc, data, y);

  // Add page numbers
  addPageNumbers(doc);

  return doc;
}

/**
 * Draw header with title and date
 */
function drawHeader(doc: jsPDF, _data: BankerReconPdfData, startY: number): number {
  const y = startY;

  // Lendwise logo text
  doc.setFont('times', 'italic');
  doc.setFontSize(24);
  doc.setTextColor(...COLORS.black);
  doc.text('Lendwise', PAGE.marginLeft, y + 18);

  // Title banner
  const bannerY = y + 30;
  doc.setFillColor(...COLORS.brandGreen);
  doc.rect(PAGE.marginLeft, bannerY, PAGE.contentWidth, 24, 'F');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.setTextColor(...COLORS.white);
  doc.text('Banker Reconciliation Summary', PAGE.width / 2, bannerY + 16, { align: 'center' });

  // Generated date (right side)
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...COLORS.grayText);
  const today = new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
  doc.text(`Generated: ${today}`, PAGE.width - PAGE.marginRight, y + 18, { align: 'right' });

  return bannerY + 35;
}

/**
 * Draw loan information section
 */
function drawLoanInfoSection(doc: jsPDF, data: BankerReconPdfData, startY: number): number {
  let y = startY;
  const { selectedLoan } = data;

  // Section header
  y = drawSectionHeader(doc, 'Loan Information', y);

  const col1X = PAGE.marginLeft + 5;
  const col1ValX = PAGE.marginLeft + 80;
  const col2X = PAGE.width / 2 + 20;
  const col2ValX = PAGE.width / 2 + 100;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...COLORS.black);

  // Left column
  doc.text('Borrower:', col1X, y);
  doc.text(`${selectedLoan.borrower_first_name || ''} ${selectedLoan.borrower_last_name || ''}`.trim() || '—', col1ValX, y);

  y += 12;
  doc.text('Loan Number:', col1X, y);
  doc.setFont('helvetica', 'bold');
  doc.text(selectedLoan.trans_details_loan_number || '—', col1ValX, y);
  doc.setFont('helvetica', 'normal');

  y += 12;
  doc.text('Loan Amount:', col1X, y);
  doc.text(selectedLoan.trans_details_loan_amt ? formatCurrency(selectedLoan.trans_details_loan_amt) : '—', col1ValX, y);

  // Right column (same Y positions)
  let rightY = startY + 14;
  doc.text('Loan Officer:', col2X, rightY);
  doc.text(`${selectedLoan.loan_officer_first_name || ''} ${selectedLoan.loan_officer_last_name || ''}`.trim() || '—', col2ValX, rightY);

  rightY += 12;
  doc.text('Closing Date:', col2X, rightY);
  doc.text(formatDate(data.keyDates.closingDate), col2ValX, rightY);

  return y + 20;
}

/**
 * Draw key dates section
 */
function drawKeyDatesSection(doc: jsPDF, data: BankerReconPdfData, startY: number): number {
  let y = startY;
  const { keyDates } = data;

  y = drawSectionHeader(doc, 'Key Dates', y);

  const colWidth = PAGE.contentWidth / 4;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);

  // Labels
  doc.setTextColor(...COLORS.grayText);
  doc.text('Closing Date', PAGE.marginLeft + 5, y);
  doc.text('Funds to Escrow', PAGE.marginLeft + colWidth + 5, y);
  doc.text('Funds Received', PAGE.marginLeft + colWidth * 2 + 5, y);
  doc.text('Days on Warehouse', PAGE.marginLeft + colWidth * 3 + 5, y);

  y += 12;

  // Values
  doc.setTextColor(...COLORS.black);
  doc.text(formatDate(keyDates.closingDate), PAGE.marginLeft + 5, y);
  doc.text(formatDate(keyDates.fundsToEscrow), PAGE.marginLeft + colWidth + 5, y);
  doc.text(formatDate(keyDates.fundsReceived), PAGE.marginLeft + colWidth * 2 + 5, y);

  const warehouseDays = keyDates.fundsToEscrow && keyDates.fundsReceived
    ? `${calculateDaysBetween(keyDates.fundsToEscrow, keyDates.fundsReceived)} days`
    : '—';
  doc.setTextColor(...COLORS.red);
  doc.text(warehouseDays, PAGE.marginLeft + colWidth * 3 + 5, y);

  return y + 20;
}

/**
 * Draw pricing summary section
 */
function drawPricingSummary(doc: jsPDF, data: BankerReconPdfData, startY: number): number {
  let y = startY;
  const { selectedLoan, pricing, purchaseAdjustments } = data;

  y = drawSectionHeader(doc, 'Pricing Summary', y);

  // Calculate pricing values
  const adjustments = selectedLoan?.rate_lock_buy_side_adjustments || [];
  const marginAdjustments = adjustments.filter(adj => adj.priceAdjustmentType === 'ProfitMargin');
  const totalMargin = marginAdjustments.reduce((sum, adj) => sum + Math.abs(adj.rate), 0);
  const encompassBuyPrice = selectedLoan?.rate_lock_total_buy_price || 0;
  const investorLockPrice = encompassBuyPrice + totalMargin;
  const variance = pricing.actualPricePercent - investorLockPrice;
  const varianceIsPositive = variance >= 0;

  const col1X = PAGE.marginLeft + 5;
  const col2X = PAGE.marginLeft + PAGE.contentWidth / 3 + 5;
  const col3X = PAGE.marginLeft + (PAGE.contentWidth / 3) * 2 + 5;

  doc.setFontSize(8);

  // Column 1: Encompass Price
  doc.setTextColor(...COLORS.grayText);
  doc.text('ENCOMPASS PRICE', col1X, y);
  y += 12;

  doc.setTextColor(...COLORS.black);
  doc.setFont('helvetica', 'normal');
  doc.text(`Total Price Locked: ${encompassBuyPrice ? encompassBuyPrice.toFixed(4) : '—'}`, col1X, y);
  y += 10;
  doc.text(`Margin: +${totalMargin.toFixed(4)}`, col1X, y);
  y += 10;
  doc.setFont('helvetica', 'bold');
  doc.text(`Investor Lock: ${investorLockPrice ? investorLockPrice.toFixed(4) : '—'}`, col1X, y);

  // Column 2: Actual Purchase Price
  let col2Y = startY + 14;
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLORS.grayText);
  doc.text('ACTUAL PURCHASE PRICE', col2X, col2Y);
  col2Y += 12;

  doc.setTextColor(...COLORS.black);
  doc.text(`Investor Lock: ${investorLockPrice ? investorLockPrice.toFixed(4) : '—'}`, col2X, col2Y);
  col2Y += 10;

  // Purchase adjustments
  const totalAdj = purchaseAdjustments.reduce((sum, adj) => sum + adj.basisPoints, 0);
  doc.text(`Adjustments: ${totalAdj >= 0 ? '+' : ''}${totalAdj.toFixed(4)}`, col2X, col2Y);
  col2Y += 10;
  doc.setFont('helvetica', 'bold');
  doc.text(`Actual Price: ${pricing.actualPricePercent ? pricing.actualPricePercent.toFixed(4) : '—'}`, col2X, col2Y);

  // Column 3: Variance
  let col3Y = startY + 14;
  doc.setFont('helvetica', 'normal');
  const varianceColor = varianceIsPositive ? COLORS.green : COLORS.red;
  doc.setTextColor(...varianceColor);
  doc.text(varianceIsPositive ? 'PICKUP' : 'LOSS', col3X, col3Y);
  col3Y += 12;

  doc.setTextColor(...COLORS.black);
  const loanAmount = selectedLoan?.trans_details_loan_amt || 0;
  const varianceAmount = loanAmount * variance / 100;

  doc.text(`Variance: ${variance >= 0 ? '+' : ''}${variance.toFixed(4)}`, col3X, col3Y);
  col3Y += 10;
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...varianceColor);
  doc.text(`Amount: ${variance >= 0 ? '+' : ''}${formatCurrency(varianceAmount)}`, col3X, col3Y);

  return y + 25;
}

/**
 * Draw summary totals (Revenue, Costs, Net Profit)
 */
function drawSummaryTotals(doc: jsPDF, data: BankerReconPdfData, startY: number): number {
  let y = startY;

  y = drawSectionHeader(doc, 'Summary', y);

  const boxWidth = (PAGE.contentWidth - 20) / 3;
  const boxHeight = 50;

  const { netProfit, totalRevenueForDisplay: totalRevenueDisplay, totalCostsForDisplay: totalCostsDisplay } = data;

  // Total Revenue Box
  doc.setFillColor(236, 253, 245); // green-50
  doc.setDrawColor(...COLORS.green);
  doc.setLineWidth(1);
  doc.rect(PAGE.marginLeft, y, boxWidth, boxHeight, 'FD');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(...COLORS.grayText);
  doc.text('TOTAL REVENUE', PAGE.marginLeft + 5, y + 12);

  doc.setFontSize(14);
  doc.setTextColor(...COLORS.green);
  doc.text(`+${formatCurrency(totalRevenueDisplay)}`, PAGE.marginLeft + 5, y + 32);

  // Total Costs Box
  const costBoxX = PAGE.marginLeft + boxWidth + 10;
  doc.setFillColor(254, 242, 242); // red-50
  doc.setDrawColor(...COLORS.red);
  doc.rect(costBoxX, y, boxWidth, boxHeight, 'FD');

  doc.setFontSize(8);
  doc.setTextColor(...COLORS.grayText);
  doc.text('TOTAL COSTS', costBoxX + 5, y + 12);

  doc.setFontSize(14);
  doc.setTextColor(...COLORS.red);
  doc.text(`-${formatCurrency(totalCostsDisplay)}`, costBoxX + 5, y + 32);

  // Net Profit Box
  const profitBoxX = PAGE.marginLeft + (boxWidth + 10) * 2;
  doc.setFillColor(236, 254, 255); // cyan-50
  doc.setDrawColor(...COLORS.cyan);
  doc.rect(profitBoxX, y, boxWidth, boxHeight, 'FD');

  doc.setFontSize(8);
  doc.setTextColor(...COLORS.grayText);
  doc.text('NET PROFIT', profitBoxX + 5, y + 12);

  doc.setFontSize(14);
  doc.setTextColor(...COLORS.cyan);
  doc.text(formatCurrency(netProfit), profitBoxX + 5, y + 32);

  // Basis points
  const loanAmount = data.selectedLoan?.trans_details_loan_amt || 0;
  if (loanAmount > 0) {
    doc.setFontSize(10);
    doc.text(`${((netProfit / loanAmount) * 100).toFixed(3)} pts`, profitBoxX + 5, y + 45);
  }

  return y + boxHeight + 15;
}

/**
 * Draw LO Compensation section
 */
function drawCompensationSection(doc: jsPDF, data: BankerReconPdfData, startY: number): number {
  let y = startY;
  const { compensation, loPayout, totalReimbursable, volumeBonusAmount = 0 } = data;

  y = drawSectionHeader(doc, 'LO Compensation', y);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...COLORS.black);

  const col1X = PAGE.marginLeft + 5;
  const col2X = PAGE.marginLeft + 150;
  const col3X = PAGE.marginLeft + 300;

  // Row 1
  doc.setTextColor(...COLORS.grayText);
  doc.text('Compensation Type', col1X, y);
  doc.text('Value', col2X, y);
  doc.text('Total LO Payout', col3X, y);

  y += 12;

  doc.setTextColor(...COLORS.black);
  doc.text(compensation.type === 'flat_fee' ? 'Flat Fee' : 'Percentage', col1X, y);
  doc.text(compensation.type === 'flat_fee'
    ? formatCurrency(compensation.value)
    : `${compensation.value}%`, col2X, y);

  const totalLoPayout = loPayout + totalReimbursable + volumeBonusAmount;
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...(totalLoPayout < 0 ? COLORS.red : COLORS.green));
  doc.text(formatCurrency(totalLoPayout), col3X, y);

  // Breakdown if reimbursables exist
  if (loPayout < 0 || totalReimbursable !== 0) {
    y += 12;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...COLORS.grayText);
    doc.text(`Base net: ${formatCurrency(loPayout)} + Reimbursables: ${formatCurrency(totalReimbursable)}`, col3X, y);
  }

  if (volumeBonusAmount > 0) {
    y += 14;
    const installment = data.volumeBonusInstallment;
    const label = installment ? `Volume Bonus - ${installment.number} of ${installment.total}` : 'Volume Bonus';
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...COLORS.black);
    doc.text(label, col1X, y);
    doc.text(formatCurrency(volumeBonusAmount), col3X, y);
    y += 11;
    doc.setFontSize(8);
    doc.setTextColor(...COLORS.grayText);
    doc.text(installment?.allocated ? 'Allocated payable; this summary is not proof of payment.' : 'Preview only; allocated when this reconciliation is finalized.', col1X, y);
  }

  // Override detail rows
  if (data.overrides.length > 0) {
    y += 14;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...COLORS.grayText);
    doc.text('OVERRIDES', col1X, y);
    y += 10;

    const loanAmount = data.selectedLoan?.trans_details_loan_amt || 0;
    for (const override of data.overrides) {
      if (override.bps <= 0) {
continue;
}
      const amount = loanAmount * override.bps / 10000;
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...COLORS.black);
      doc.text(`${override.recipientName || 'Recipient'} (${override.bps} bps)`, col1X, y);
      doc.setTextColor(...COLORS.red);
      doc.text(`-${formatCurrency(amount)}`, col3X, y);
      y += 10;
    }
    if (data.overrides.length > 1) {
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...COLORS.red);
      doc.text(`Total Overrides: -${formatCurrency(data.totalOverrideAmount)}`, col1X, y);
      y += 10;
    }
  }

  return y + 20;
}

/**
 * Draw costs breakdown section
 */
function drawCostsBreakdown(doc: jsPDF, data: BankerReconPdfData, startY: number): number {
  let y = startY;
  const { costs, totalWarehouseCosts, totalInvestorCosts } = data;
  const loanType = data.selectedLoan?.trans_details_loan_type || null;
  const isGovernmentFundingCost = (item: { id: string; description: string }) =>
    isGovernmentFundingPassThrough({
      category: 'investor_cost',
      line_type: item.id,
      description: item.description
    }, loanType);
  const isBuydownCost = (item: { id: string; description: string }) =>
    isBuydownPassThrough({
      category: 'investor_cost',
      line_type: item.id,
      description: item.description
    });
  const investorExpenseItems = costs.investor
    .filter(c => c.amount > 0 && !c.id.startsWith('escrow_deposit') && !isGovernmentFundingCost(c) && !isBuydownCost(c));
  const escrowPassThrough = costs.investor
    .filter(c => c.id.startsWith('escrow_deposit'))
    .reduce((sum, c) => sum + c.amount, 0);
  const governmentFundingPassThrough = costs.investor
    .filter(isGovernmentFundingCost)
    .reduce((sum, c) => sum + c.amount, 0);
  const buydownPassThrough = costs.investor
    .filter(isBuydownCost)
    .reduce((sum, c) => sum + c.amount, 0);

  y = drawSectionHeader(doc, 'Costs Breakdown', y);

  const midX = PAGE.width / 2;

  // Warehouse Costs (Left)
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...COLORS.black);
  doc.text('Warehouse Costs', PAGE.marginLeft + 5, y);

  // Investor Costs (Right)
  doc.text('Investor Costs', midX + 5, y);

  y += 12;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);

  // Warehouse items
  let leftY = y;
  costs.warehouse.filter(c => c.amount > 0).forEach(item => {
    doc.setTextColor(...COLORS.black);
    doc.text(item.description, PAGE.marginLeft + 5, leftY);
    doc.text(formatCurrency(item.amount), PAGE.marginLeft + 160, leftY, { align: 'right' });
    leftY += 10;
  });

  // FlexClose credit
  if (costs.flexcloseCredit > 0) {
    doc.setTextColor(...COLORS.green);
    doc.text('FlexClose Credit', PAGE.marginLeft + 5, leftY);
    doc.text(`(${formatCurrency(costs.flexcloseCredit)})`, PAGE.marginLeft + 160, leftY, { align: 'right' });
    leftY += 10;
  }

  // Warehouse total
  leftY += 2;
  doc.setDrawColor(...COLORS.border);
  doc.line(PAGE.marginLeft + 5, leftY, PAGE.marginLeft + 160, leftY);
  leftY += 10;
  doc.setFont('helvetica', 'bold');
  doc.text('Total:', PAGE.marginLeft + 5, leftY);
  doc.setTextColor(...COLORS.red);
  const netWarehouseCosts = totalWarehouseCosts - costs.flexcloseCredit;
  doc.text(formatCurrency(netWarehouseCosts), PAGE.marginLeft + 160, leftY, { align: 'right' });

  // Investor items
  let rightY = y;
  investorExpenseItems.forEach(item => {
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLORS.black);
    doc.text(item.description, midX + 5, rightY);
    doc.text(formatCurrency(item.amount), midX + 160, rightY, { align: 'right' });
    rightY += 10;
  });

  if (investorExpenseItems.length === 0) {
    doc.setTextColor(...COLORS.grayText);
    doc.text('No investor costs', midX + 5, rightY);
    rightY += 10;
  }

  if (escrowPassThrough > 0) {
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLORS.grayText);
    doc.text('Escrow Pass-Through (wash)', midX + 5, rightY);
    doc.text(formatCurrency(escrowPassThrough), midX + 160, rightY, { align: 'right' });
    rightY += 10;
  }

  if (governmentFundingPassThrough > 0) {
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLORS.grayText);
    doc.text('Government Funding Pass-Through', midX + 5, rightY);
    doc.text(formatCurrency(governmentFundingPassThrough), midX + 160, rightY, { align: 'right' });
    rightY += 10;
  }

  if (buydownPassThrough > 0) {
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLORS.grayText);
    doc.text('Temporary Subsidy Buydown Pass-Through', midX + 5, rightY);
    doc.text(formatCurrency(buydownPassThrough), midX + 160, rightY, { align: 'right' });
    rightY += 10;
  }

  // Investor total
  rightY += 2;
  doc.line(midX + 5, rightY, midX + 160, rightY);
  rightY += 10;
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.black);
  doc.text('Total:', midX + 5, rightY);
  doc.setTextColor(...COLORS.red);
  doc.text(formatCurrency(totalInvestorCosts - escrowPassThrough - governmentFundingPassThrough - buydownPassThrough), midX + 160, rightY, { align: 'right' });

  return Math.max(leftY, rightY) + 20;
}

/**
 * Draw revenue breakdown section (Loan Revenue only)
 */
function drawRevenueBreakdown(doc: jsPDF, data: BankerReconPdfData, startY: number): number {
  let y = startY;
  const { revenue, totalLoanRevenue } = data;

  y = drawSectionHeader(doc, 'Loan Revenue', y);

  // Loan Revenue header
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...COLORS.black);

  y += 4;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);

  // Loan revenue items
  revenue.loan.filter(r => r.amount > 0).forEach(item => {
    doc.setTextColor(...COLORS.black);
    const label = item.reimbursable ? `${item.description} (R)` : item.description;
    doc.text(label, PAGE.marginLeft + 5, y);
    doc.text(formatCurrency(item.amount), PAGE.marginLeft + 200, y, { align: 'right' });
    y += 10;
  });

  // Loan revenue total
  y += 2;
  doc.setDrawColor(...COLORS.border);
  doc.line(PAGE.marginLeft + 5, y, PAGE.marginLeft + 200, y);
  y += 10;
  doc.setFont('helvetica', 'bold');
  doc.text('Total:', PAGE.marginLeft + 5, y);
  doc.setTextColor(...COLORS.green);
  doc.text(formatCurrency(totalLoanRevenue), PAGE.marginLeft + 200, y, { align: 'right' });

  // Lender Credit if exists
  if (revenue.lenderCredit > 0) {
    y += 15;
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLORS.red);
    doc.text(`Lender Credit Given: -${formatCurrency(revenue.lenderCredit)}`, PAGE.marginLeft + 5, y);
  }
  if (revenue.cureAppliedLenderCredit > 0) {
    y += 15;
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLORS.red);
    doc.text(`Cure Applied to Lender Credit: -${formatCurrency(revenue.cureAppliedLenderCredit)}`, PAGE.marginLeft + 5, y);
  }
  // "Warehouse withheld MI": the expense leg of the MI premium (the revenue leg is listed above).
  const miPremiumExpense = data.lineItems.find(
    (item) => item.category === 'lender_cost' && item.line_type === MI_PREMIUM_REMITTANCE_LINE_TYPE && item.amount > 0
  );
  if (miPremiumExpense) {
    y += 15;
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLORS.red);
    doc.text(`${miPremiumExpense.description}: -${formatCurrency(miPremiumExpense.amount)}`, PAGE.marginLeft + 5, y);
  }

  if (revenue.lenderCredit > 0 || revenue.cureAppliedLenderCredit > 0 || miPremiumExpense) {
    return y + 15;
  }

  return y + 20;
}

// Manual adjustment amounts: income adds to profit, an expense subtracts, a pass-through has no P&L sign.
const MANUAL_AMOUNT_STYLE: Record<ManualAdjustmentKind, { sign: string; color: [number, number, number] }> = {
  income: { sign: '+', color: COLORS.green },
  expense: { sign: '-', color: COLORS.red },
  pass_through: { sign: '', color: COLORS.black }
};

/**
 * Draw the manual adjustments the reviewer entered (nothing when there are none). Every entry moved
 * through the warehouse wire; each line names its type (income, expense or pass-through).
 */
function drawManualAdjustments(doc: jsPDF, data: BankerReconPdfData, startY: number): number {
  const entries = data.lineItems
    .map(manualAdjustmentFromLineItem)
    .filter((entry): entry is ManualReconAdjustment => entry !== null);
  if (entries.length === 0) {
    return startY;
  }

  let y = drawSectionHeader(doc, 'Manual Adjustments', startY);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  y += 4;

  for (const entry of entries) {
    const label = `${entry.description} · ${MANUAL_ADJUSTMENT_KIND_LABELS[entry.kind]}`;
    const lines = doc.splitTextToSize(label, 150) as string[];
    if (y + lines.length * 10 > PAGE.height - 60) {
      doc.addPage();
      y = PAGE.marginTop;
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
    }
    doc.setTextColor(...COLORS.black);
    doc.text(lines, PAGE.marginLeft + 5, y);
    doc.setTextColor(...MANUAL_AMOUNT_STYLE[entry.kind].color);
    doc.text(`${MANUAL_AMOUNT_STYLE[entry.kind].sign}${formatCurrency(entry.amount)}`, PAGE.marginLeft + 200, y, { align: 'right' });
    y += lines.length * 10;
  }

  return y + 10;
}

/**
 * Draw wire reconciliation section
 */
function drawWireReconciliation(doc: jsPDF, data: BankerReconPdfData, startY: number): number {
  let y = startY;
  const { selectedLoan, pricing, wireFromInvestor, wireFromWarehouse, totalWarehouseCosts, totalInvestorRevenue, totalInvestorCosts, revenue } = data;

  y = drawSectionHeader(doc, 'Wire Reconciliation', y, COLORS.violet);

  const midX = PAGE.width / 2;

  // Calculate expected values
  const loanAmount = selectedLoan?.trans_details_loan_amt || 0;
  const investorAudit = calculateInvestorWireAudit({
    loanAmount,
    actualPurchasePricePercent: pricing.actualPricePercent,
    totalInvestorRevenue,
    totalInvestorCosts,
    servicingPrincipalCollected: data.servicingPrincipalCollected || 0,
    wireFromInvestor
  });
  const expectedInvestorWire = investorAudit.displayExpectedInvestorWire;
  const investorDifference = investorAudit.displayDifference;
  const isInvestorBalanced = investorAudit.isInvestorBalanced;

  // Cash-flow formula via shared utility
  const haircutPercent = data.costs.warehouseHaircutPercent || 0;
  const escrowDeposit = selectedLoan?.fees_initial_escrow_deposit || 0;
  const shortageFunded = data.costs.warehouse.find(c => c.id === 'shortage_funded')?.amount || 0;
  const processingFee = revenue.loan.find(r => r.id === 'processing_fee')?.amount || 0;
  const underwritingFee = revenue.loan.find(r => r.id === 'underwriting_fee')?.amount || 0;
  const interestIncome = revenue.loan.find(r => r.id === 'interest_charged')?.amount || 0;
  const selectedLoanRecord = selectedLoan as unknown as Record<string, unknown>;
  const leFields = selectedLoanRecord.le_fields as Record<string, unknown> | null | undefined;
  const cdCreditNettedToWarehouseAdvance = calculateCdCreditNettedToWarehouseAdvance({
    rawLenderCredit4794: selectedLoanRecord.trans_details_non_specific_lender_credit,
    lenpcc: leFields?.LENPCC,
    persistedLenderCredit: revenue.lenderCredit,
    persistedLenderCreditIsCurrentPnlBasis: false,
    cureAppliedLenderCredit: revenue.cureAppliedLenderCredit,
    lenderCreditNetsToWarehouseAdvance: revenue.lenderCreditNetsToWarehouseAdvance,
    cureAppliedLenderCreditNetsToWarehouseAdvance: revenue.cureAppliedLenderCreditNetsToWarehouseAdvance,
    lenderCreditTimingBasis: data.lenderCreditTimingBasis ?? (data.status === 'draft' ? 'total' : 'field4794')
  });
  const { expectedBalance, warehouseDifference, isWarehouseBalanced, basisModeUsed, manualWireAdjustment } =
    calculateExpectedBalance({
      loanAmount,
      processingFee,
      underwritingFee,
      interestIncome,
      escrowDeposit,
      lineItems: data.lineItems,
      loanType: selectedLoan?.trans_details_loan_type || null,
      fhaUfmipWarehousePremium: data.fhaUfmipBridge.warehousePremiumAmount,
      haircutPercent,
      wireFromInvestor,
      totalWarehouseCosts,
      shortageFunded,
      flexcloseCredit: data.costs.flexcloseCredit,
      retainedClosingFeeBasis: data.retainedClosingFeeBasis ?? (data.status === 'draft' ? 'expandedLeFees' : 'legacy'),
      basisMode: 'autoCdCashRequired',
      cdCreditNettedToWarehouseAdvance
    }, wireFromWarehouse);

  doc.setFontSize(8);

  // Left Column: Investor → Warehouse
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.violet);
  doc.text('INVESTOR → WAREHOUSE', PAGE.marginLeft + 5, y);

  y += 12;
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLORS.grayText);
  doc.text('Wire from Investor:', PAGE.marginLeft + 5, y);
  doc.setTextColor(...COLORS.black);
  doc.text(wireFromInvestor > 0 ? formatCurrency(wireFromInvestor) : '—', PAGE.marginLeft + 100, y);

  y += 10;
  doc.setTextColor(...COLORS.grayText);
  doc.text('Expected:', PAGE.marginLeft + 5, y);
  doc.setTextColor(...COLORS.black);
  doc.text(pricing.actualPricePercent > 0 ? formatCurrency(expectedInvestorWire) : '—', PAGE.marginLeft + 100, y);

  y += 10;
  doc.setTextColor(...COLORS.grayText);
  doc.text('Difference:', PAGE.marginLeft + 5, y);
  if (wireFromInvestor > 0 && pricing.actualPricePercent > 0) {
    const investorDiffColor = isInvestorBalanced ? COLORS.green : COLORS.red;
    doc.setTextColor(...investorDiffColor);
    doc.setFont('helvetica', 'bold');
    doc.text(`${formatCurrency(investorDifference)} ${isInvestorBalanced ? '✓ BALANCED' : 'DIFFERENCE'}`, PAGE.marginLeft + 100, y);
  } else {
    doc.setTextColor(...COLORS.grayText);
    doc.text('—', PAGE.marginLeft + 100, y);
  }

  // Right Column: Warehouse → Us
  let rightY = startY + 14;
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.violet);
  doc.text('WAREHOUSE → US', midX + 5, rightY);

  rightY += 12;
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLORS.grayText);
  doc.text('Wire from Warehouse:', midX + 5, rightY);
  doc.setTextColor(...COLORS.black);
  doc.text(wireFromWarehouse > 0 ? formatCurrency(wireFromWarehouse) : '—', midX + 110, rightY);

  rightY += 10;
  doc.setTextColor(...COLORS.grayText);
  doc.text(basisModeUsed === 'cdCashRequired' ? 'Expected (Cash Needed):' : 'Expected:', midX + 5, rightY);
  doc.setTextColor(...COLORS.black);
  doc.text(formatCurrency(expectedBalance), midX + 110, rightY);

  if (manualWireAdjustment !== 0) {
    rightY += 10;
    doc.setTextColor(...COLORS.grayText);
    doc.text('Incl. manual adjustments:', midX + 5, rightY);
    doc.setTextColor(...COLORS.black);
    doc.text(`${manualWireAdjustment > 0 ? '+' : '-'}${formatCurrency(Math.abs(manualWireAdjustment))}`, midX + 110, rightY);
  }

  rightY += 10;
  doc.setTextColor(...COLORS.grayText);
  doc.text('Difference:', midX + 5, rightY);
  if (wireFromWarehouse > 0) {
    const warehouseDiffColor = isWarehouseBalanced ? COLORS.green : COLORS.red;
    doc.setTextColor(...warehouseDiffColor);
    doc.setFont('helvetica', 'bold');
    doc.text(`${formatCurrency(warehouseDifference)} ${isWarehouseBalanced ? '✓ BALANCED' : 'DIFFERENCE'}`, midX + 110, rightY);
  } else {
    doc.setTextColor(...COLORS.grayText);
    doc.text('—', midX + 110, rightY);
  }

  return Math.max(y, rightY) + 20;
}

function drawFhaUfmipSection(doc: jsPDF, data: BankerReconPdfData, startY: number): number {
  let y = drawSectionHeader(doc, 'FHA Upfront Mortgage Insurance', startY, COLORS.cyan);
  const bridge = data.fhaUfmipBridge;
  const leftX = PAGE.marginLeft + 5;
  const valueX = PAGE.marginLeft + 145;
  const rightX = PAGE.width / 2 + 5;
  const rightValueX = PAGE.width / 2 + 145;

  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLORS.grayText);
  doc.text('Flow:', leftX, y);
  doc.text('Premium collected (969):', rightX, y);
  doc.setTextColor(...COLORS.black);
  doc.text(bridge.flowMode.replace(/_/g, ' '), valueX, y);
  doc.text(formatCurrency(bridge.premiumAmount || 0), rightValueX, y);

  y += 11;
  doc.setTextColor(...COLORS.grayText);
  doc.text('Financed (1045):', leftX, y);
  doc.text('Borrower cash portion:', rightX, y);
  doc.setTextColor(...COLORS.black);
  doc.text(
    bridge.financedAmount === null ? '—' : formatCurrency(bridge.financedAmount),
    valueX,
    y
  );
  doc.text(
    bridge.financedAmount === null ? '—' : formatCurrency(bridge.borrowerCashAmount),
    rightValueX,
    y
  );

  y += 11;
  doc.setTextColor(...COLORS.grayText);
  doc.text('HUD remittance (3081):', leftX, y);
  doc.text('Remitted date / by:', rightX, y);
  doc.setTextColor(...COLORS.black);
  doc.text(
    bridge.remittedAmount === null ? '—' : formatCurrency(bridge.remittedAmount),
    valueX,
    y
  );
  doc.text(
    `${formatDate(bridge.remittedDate)} / ${bridge.remittedBy || '—'}`,
    rightValueX,
    y,
    { maxWidth: PAGE.width - PAGE.marginRight - rightValueX }
  );

  y += 11;
  doc.setTextColor(...COLORS.grayText);
  doc.text('Verification (3082):', leftX, y);
  doc.text('Company variance:', rightX, y);
  doc.setTextColor(...COLORS.black);
  doc.text(bridge.verificationCode || '—', valueX, y);
  doc.setTextColor(...(bridge.companyVarianceAmount > 0 ? COLORS.red : COLORS.green));
  doc.setFont('helvetica', 'bold');
  doc.text(formatCurrency(bridge.companyVarianceAmount), rightValueX, y);

  return y + 20;
}

/**
 * Draw document status section
 */
function drawDocumentStatus(doc: jsPDF, data: BankerReconPdfData, startY: number): void {
  let y = startY;
  const { uploadedDocuments } = data;

  y = drawSectionHeader(doc, 'Document Status', y);

  const warehouseDoc = uploadedDocuments.find(d => d.documentType === 'warehouse_reconciliation');
  const purchaseDoc = uploadedDocuments.find(d => d.documentType === 'purchase_advice');

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);

  // Warehouse Reconciliation
  doc.setTextColor(...COLORS.black);
  doc.text('Warehouse Reconciliation:', PAGE.marginLeft + 5, y);
  const warehouseStatusColor = warehouseDoc ? COLORS.green : COLORS.red;
  doc.setTextColor(...warehouseStatusColor);
  doc.text(warehouseDoc ? 'Uploaded' : 'Not uploaded', PAGE.marginLeft + 140, y);

  // Purchase Advice
  y += 12;
  doc.setTextColor(...COLORS.black);
  doc.text('Purchase Advice:', PAGE.marginLeft + 5, y);
  const purchaseStatusColor = purchaseDoc ? COLORS.green : COLORS.red;
  doc.setTextColor(...purchaseStatusColor);
  doc.text(purchaseDoc ? 'Uploaded' : 'Not uploaded', PAGE.marginLeft + 140, y);
}

/**
 * Draw a section header with gray background
 */
function drawSectionHeader(doc: jsPDF, title: string, y: number, color?: [number, number, number]): number {
  const headerHeight = 16;

  doc.setFillColor(...(color || COLORS.grayHeader));
  doc.setDrawColor(...COLORS.border);
  doc.setLineWidth(0.5);
  doc.rect(PAGE.marginLeft, y, PAGE.contentWidth, headerHeight, 'FD');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  const textColor = color ? COLORS.white : COLORS.black;
  doc.setTextColor(...textColor);
  doc.text(title, PAGE.marginLeft + 5, y + 11);

  return y + headerHeight + 8;
}

/**
 * Add page numbers to all pages
 */
function addPageNumbers(doc: jsPDF): void {
  const totalPages = doc.getNumberOfPages();

  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...COLORS.grayText);
    doc.text(
      `Page ${i} of ${totalPages}`,
      PAGE.width / 2,
      PAGE.height - 25,
      { align: 'center' }
    );
  }
}

/**
 * Download the PDF directly
 */
export function downloadBankerReconPDF(data: BankerReconPdfData): void {
  const doc = generateBankerReconPDF(data);
  const loanNumber = data.selectedLoan.trans_details_loan_number || 'Unknown';
  const today = new Date().toISOString().split('T')[0];
  const filename = `Banker_Recon_${loanNumber}_${today}.pdf`;

  doc.save(filename);
}
